
import * as React from 'react';
import './editModalDropdown.scss';
import Select from '@payscale/design/lib/select';
import { ILabEmployee, IJob } from 'typings/employeeErrorData';
import { teamErrorData } from 'utils/MobxDataManager/singletons';

interface EditModalDropdownProps {
    type: string;
    labEmployee: Nullable<ILabEmployee>;
    isSending: boolean;
    errorDetails: { [id: string]: string };
    onUpdateCallBack: (labEmployee: Nullable<ILabEmployee>, errorDetails: { [id: string]: string }) => void
}

interface EditModalDropdownState {
    jobs: IJob[];
}

export default class EditModalDropdown extends React.Component<EditModalDropdownProps, EditModalDropdownState> {
    constructor(props: EditModalDropdownProps) {
        super(props);
        this.handleSelectPayTypeChange = this.handleSelectPayTypeChange.bind(this);
        this.handleSelectJobChange = this.handleSelectJobChange.bind(this);
        this.renderWarning = this.renderWarning.bind(this);

        this.state = {
            jobs: []
        };
    }

    async componentDidMount(): Promise<void> {
        const jobs = await teamErrorData.getJobs();
        this.setState({jobs});
    }

    render(): React.ReactNode {
        return (
            <div className="nomst-edit-modal-dropdown">
                {this.props.type === 'jobs' && this.renderJobsDropDown()}
                {this.props.type === 'payType' && this.renderPayTypeField()}
            </div>
        );
    }

    private renderJobsDropDown(): React.ReactNode {
        const { labEmployee, errorDetails } = this.props;
        const jobInfo = this.state.jobs.find(job => job.orgJobId === labEmployee?.orgJobId);
        const jobValue = errorDetails.orgJobTitle ? '' : `${jobInfo?.title}-${jobInfo?.orgJobId}-${jobInfo?.currency}`;
        const classNameWithErrorModifier = errorDetails.orgJobTitle ? 'nomst-edit-modal-dropdown__field--error' : '';

        return this.renderDropDown(
            'jobTitle',
            'Job Title',
            `nomst-edit-modal-dropdown__field ${classNameWithErrorModifier}`,
            classNameWithErrorModifier,
            'edit-employee-job-title',
            jobValue,
            this.handleSelectJobChange,
            teamErrorData.createJobsOptions(),
            this.props.isSending,
            errorDetails.orgJobTitle,
            'Select Job Title');

    }

    private renderPayTypeField(): React.ReactNode {
        const { labEmployee, errorDetails } = this.props;
        const payTypeValue = errorDetails.annualOrHourly ? '--' : labEmployee?.annualOrHourly ?? '--';
        const classNameWithErrorModifier = errorDetails.annualOrHourly ? 'nomst-edit-modal-dropdown__field--error' : '';
        const error = errorDetails.annualOrHourly || (labEmployee?.annualOrHourly ? '' : 'Missing pay type.');

        return this.renderDropDown(
            'payType',
            'Pay Type',
            `nomst-edit-modal-dropdown__field ${classNameWithErrorModifier}`,
            'pxl-form-control pxl-form-control--shrink',
            'edit-employee-pay-type',
            payTypeValue,
            this.handleSelectPayTypeChange,
            teamErrorData.createMPayTypeOptions(),
            this.props.isSending,
            error,
            'Select Pay Type');
    }

    private renderDropDown(
        containerId: string,
        label: string,
        containerClassName: string,
        selectClassName: string,
        selectId: string,
        defaultvalue: string,
        onChange:(_: undefined, value: string) => void,
        options: [{ text: string, value: Nullable<string> }] | [],
        disabled: boolean,
        error: string,
        errorMessage: string): JSX.Element {
        return (
            <div id={containerId} className={containerClassName}>
                <label htmlFor={selectId} className="nomst-edit-modal-dropdown__field-label">{label}</label>
                <Select
                    className={selectClassName}
                    id={selectId}
                    value={defaultvalue}
                    onChange={onChange}
                    options={options}
                    disabled={disabled}
                />
                {error && this.renderWarning(error, errorMessage, selectId)}
            </div>
        );
    }

    private handleSelectPayTypeChange(_: undefined, value: string): void {
        const { labEmployee } = { ...this.props };
        if(value?.length && labEmployee) {
            labEmployee.annualOrHourly = value;
            let { errorDetails } = {...this.props};
            if(labEmployee?.annualOrHourly !== '--') {
                delete errorDetails['annualOrHourly'];
            } else {
                errorDetails = Object.assign( errorDetails, {'annualOrHourly':'missing'} );
            }
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private handleSelectJobChange(_: undefined, value: string): void {
        const { labEmployee } = { ...this.props };
        if(value?.length && labEmployee) {
            const title = value;
            let { errorDetails } = {...this.props};
            if(title !== '--') {
                const results = value.split('-');
                labEmployee.orgJobTitle = results[0];
                labEmployee.orgJobId =  parseInt(results[1], 10);
                labEmployee.currency = results[2];
                labEmployee.currencyIso4217 = results[2];
                delete errorDetails['orgJobTitle'];
                delete errorDetails['currency'];
            } else {
                errorDetails = Object.assign( errorDetails, {'orgJobTitle':'missing', 'currency': 'missing'} );
            }
            this.props.onUpdateCallBack(labEmployee, errorDetails);
        }
    }

    private renderWarning(
        error: Nullable<string>,
        errorMessage: Nullable<string>,
        htmlFor: string): JSX.Element {
        const errorToDisplay = error ? errorMessage : '';

        return (
            <label htmlFor={htmlFor} className="nomst-edit-modal-dropdown__field-warning">{errorToDisplay}</label>
        );
    }
}
